const NON_TEXT_INPUT_TYPES = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit"
]);

// Clearance above sticky bottom bars (e.g. the trip total) that hug the keyboard.
const REVEAL_BOTTOM_MARGIN = 90;
// Clearance below the sticky app header.
const REVEAL_TOP_MARGIN = 76;
// The on-screen keyboard animates in; re-check until the viewport has settled.
const REVEAL_DELAYS_MS = [0, 120, 320, 650];

function isTextEntry(target: unknown): target is HTMLElement {
  if (target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLInputElement) return !NON_TEXT_INPUT_TYPES.has(target.type);
  return target instanceof HTMLElement && target.isContentEditable;
}

/**
 * The nearest ancestor that can actually scroll `field` into view, or null when
 * the document itself is the scroller.
 *
 * Needed because a page can legitimately lock the document (#1072: the Home Map
 * frame does exactly that, at every width now). There, `window.scrollBy` is a
 * no-op and focusing a field on a phone leaves it under the keyboard — while
 * before the frame existed the same call "worked" only by scrolling the drawing
 * off screen, which is the same bug wearing a hat.
 */
function scrollableAncestor(field: HTMLElement): HTMLElement | null {
  for (let node = field.parentElement; node; node = node.parentElement) {
    if (node === document.body || node === document.documentElement) break;
    const style = window.getComputedStyle(node);
    const scrolls = /auto|scroll|overlay/.test(`${style.overflowY} ${style.overflow}`);
    if (scrolls && node.scrollHeight > node.clientHeight + 1) return node;
  }
  return null;
}

function revealFocusedField() {
  const field = document.activeElement;
  if (!isTextEntry(field) || !field.isConnected) return;

  const viewport = window.visualViewport;
  const viewTop = viewport ? viewport.offsetTop : 0;
  const viewBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
  const rect = field.getBoundingClientRect();

  let delta = 0;
  if (rect.bottom > viewBottom - REVEAL_BOTTOM_MARGIN) {
    delta = rect.bottom - (viewBottom - REVEAL_BOTTOM_MARGIN);
    // Never push the field's top up out of view for tall fields / short viewports.
    delta = Math.min(delta, Math.max(0, rect.top - (viewTop + REVEAL_TOP_MARGIN)));
  } else if (rect.top < viewTop + REVEAL_TOP_MARGIN) {
    delta = rect.top - (viewTop + REVEAL_TOP_MARGIN);
  }
  if (Math.abs(delta) < 2) return;

  // Prefer a real scrolling container. Falling back to the window keeps every
  // ordinary page behaving exactly as before — this is an added branch, not a
  // replacement.
  const container = scrollableAncestor(field);
  if (container) container.scrollTop += delta;
  else window.scrollBy(0, delta);
}

let installed = false;

/**
 * Keeps the focused text field visible when the on-screen keyboard opens (or the
 * visual viewport otherwise shrinks), scrolling just enough that the field sits
 * clear of the keyboard and any sticky bottom bar. Installed once at app startup;
 * repeat calls (e.g. HMR) are ignored so listeners never stack.
 */
export function installKeyboardReveal() {
  if (installed) return;
  installed = true;

  let pending: number[] = [];
  const schedule = () => {
    pending.forEach((id) => window.clearTimeout(id));
    pending = REVEAL_DELAYS_MS.map((ms) => window.setTimeout(revealFocusedField, ms));
  };

  document.addEventListener("focusin", (event) => {
    if (isTextEntry(event.target)) schedule();
  });
  window.visualViewport?.addEventListener("resize", () => {
    if (isTextEntry(document.activeElement)) schedule();
  });
}
